"use client"

import { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Mobile, PC, Tablet, Notebook } from "../../../views/ReactiveMode"
import PageHeader from "../../../components/common/PageHeader"
import EmptyState from "../../../components/common/EmptyState"
import {
    FaArrowLeft,
    FaSave,
    FaSpinner,
    FaImage,
    FaPlus,
    FaTrash,
    FaUsers,
    FaCalendarAlt,
    FaMapMarkerAlt,
} from "react-icons/fa"
import actionTypes from "../../../constants/actionTypes"
import DOMPurify from "dompurify"

// HTML에서 데이터를 추출하는 함수
const parseClubHTML = (htmlContent) => {
    if (!htmlContent) return null

    try {
        // DOM 파서 생성
        const parser = new DOMParser()
        const doc = parser.parseFromString(htmlContent, "text/html")

        // 각 요소에서 데이터 추출
        const clubName = doc.querySelector(".club-name")?.textContent || ""
        const clubDescription = doc.querySelector(".club-description")?.textContent || ""
        const clubLongDescription = doc.querySelector(".club-long-description")?.innerHTML || ""

        // 이미지 추출
        const mainImage = doc.querySelector(".club-image img")?.src || ""
        const galleryImages = Array.from(doc.querySelectorAll(".gallery-image")).map((img) => img.src)

        // 상세 정보 추출
        const detailItems = doc.querySelectorAll(".detail-item")
        let members = 0,
            leader = "",
            meetingTime = "",
            meetingPlace = ""

        detailItems.forEach((item) => {
            const label = item.querySelector(".detail-label")?.textContent || ""
            const value = item.querySelector(".detail-value")?.textContent || ""

            if (label.includes("회원 수")) {
                members = Number.parseInt(value.replace("명", "")) || 0
            } else if (label.includes("리더")) {
                leader = value
            } else if (label.includes("모임 시간")) {
                meetingTime = value
            } else if (label.includes("모임 장소")) {
                meetingPlace = value
            }
        })

        // 활동 내용 추출
        const activities = Array.from(doc.querySelectorAll(".activities-list li")).map((li) => li.textContent)

        return {
            name: clubName,
            description: clubDescription,
            longDescription: clubLongDescription.replace(/<br>/g, "\n"),
            imageUrl: mainImage,
            galleryImages: galleryImages,
            members: members,
            leader: leader,
            meetingTime: meetingTime,
            meetingPlace: meetingPlace,
            activities: activities.length > 0 ? activities : [""],
        }
    } catch (error) {
        console.error("HTML 파싱 오류:", error)
        return null
    }
}

// HTML 템플릿 생성 함수
const generateClubHTML = (clubData) => {
    const {
        name,
        description,
        longDescription,
        imageUrl,
        galleryImages,
        members,
        leader,
        meetingTime,
        meetingPlace,
        activities,
    } = clubData

    // 모든 텍스트 입력값 sanitize
    const safeText = (text) => {
        if (!text) return ""
        const sanitized = DOMPurify.sanitize(text, {
            ALLOWED_TAGS: [],
            ALLOWED_ATTR: [],
            ALLOW_DATA_ATTR: false,
            FORBID_SCRIPT: true,
        })
        return sanitized.replace(/\n/g, "<br>")
    }

    const safeName = safeText(name)
    const safeDescription = safeText(description)
    const safeLongDescription = safeText(longDescription)
    const safeLeader = safeText(leader)
    const safeMeetingTime = safeText(meetingTime)
    const safeMeetingPlace = safeText(meetingPlace)

    // 이미지 URL 검증
    const safeImageUrl = imageUrl && /^data:image\/[a-zA-Z]*;base64,/.test(imageUrl) ? imageUrl : ""
    const safeGalleryImages = galleryImages.filter((img) => img && /^data:image\/[a-zA-Z]*;base64,/.test(img))

    // 갤러리 이미지 HTML 생성
    const galleryHTML =
        safeGalleryImages.length > 0
            ? safeGalleryImages.map((img) => `<img src="${img}" alt="갤러리 이미지" class="gallery-image" />`).join("")
            : ""

    // 활동 내용 HTML 생성
    const activitiesHTML =
        activities && activities.length > 0
            ? activities
                .filter((activity) => activity.trim())
                .map((activity) => `<li>${safeText(activity)}</li>`)
                .join("")
            : ""

    return `
    <div class="club-detail-container">
      <header class="club-header">
        <div class="club-main-info">
          ${safeImageUrl ? `<div class="club-image"><img src="${safeImageUrl}" alt="${safeName} 대표 이미지" /></div>` : ""}
          <div class="club-info">
            <h1 class="club-name">${safeName}</h1>
            <p class="club-description">${safeDescription}</p>
          </div>
        </div>
      </header>

      <main class="club-content">
        ${
        safeLongDescription
            ? `
        <section class="club-section">
          <h2>상세 설명</h2>
          <div class="club-long-description">
            ${safeLongDescription}
          </div>
        </section>
        `
            : ""
    }

        <section class="club-section">
          <h2>동아리 정보</h2>
          <div class="club-details-grid">
            <div class="detail-item">
              <span class="detail-label"><i class="icon-users"></i> 회원 수</span>
              <span class="detail-value">${Math.max(0, Number.parseInt(members) || 0)}명</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="icon-user"></i> 리더</span>
              <span class="detail-value">${safeLeader}</span>
            </div>
            ${
        safeMeetingTime
            ? `
            <div class="detail-item">
              <span class="detail-label"><i class="icon-calendar"></i> 모임 시간</span>
              <span class="detail-value">${safeMeetingTime}</span>
            </div>
            `
            : ""
    }
            ${
        safeMeetingPlace
            ? `
            <div class="detail-item">
              <span class="detail-label"><i class="icon-location"></i> 모임 장소</span>
              <span class="detail-value">${safeMeetingPlace}</span>
            </div>
            `
            : ""
    }
          </div>
        </section>

        ${
        activitiesHTML
            ? `
        <section class="club-section">
          <h2>주요 활동</h2>
          <ul class="activities-list">
            ${activitiesHTML}
          </ul>
        </section>
        `
            : ""
    }

        ${
        galleryHTML
            ? `
        <section class="club-section">
          <h2>갤러리</h2>
          <div class="gallery-grid">
            ${galleryHTML}
          </div>
        </section>
        `
            : ""
    }
      </main>

      <style>
        .club-detail-container {
          max-width: 100%;
          margin: 0 auto;
          padding: 20px;
          font-family: 'Noto Sans KR', sans-serif;
          line-height: 1.6;
          color: #333;
        }

        .club-header {
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #f0f0f0;
        }

        .club-main-info {
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }

        .club-image img {
          width: 200px;
          height: 150px;
          object-fit: cover;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .club-info {
          flex: 1;
        }

        .club-name {
          font-size: 2.5em;
          font-weight: bold;
          margin: 0 0 15px 0;
          color: #2c3e50;
        }

        .club-description {
          font-size: 1.1em;
          color: #555;
          margin: 0;
        }

        .club-section {
          margin-bottom: 30px;
        }

        .club-section h2 {
          font-size: 1.5em;
          color: #2c3e50;
          margin-bottom: 15px;
          padding-bottom: 8px;
          border-bottom: 1px solid #eee;
        }

        .club-long-description {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          border-left: 4px solid #3498db;
        }

        .club-details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 15px;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 6px;
          border: 1px solid #e9ecef;
        }

        .detail-label {
          font-weight: 600;
          color: #495057;
        }

        .detail-value {
          color: #2c3e50;
          font-weight: 500;
        }

        .activities-list {
          list-style: none;
          padding: 0;
        }

        .activities-list li {
          background: #fff;
          margin-bottom: 8px;
          padding: 12px 16px;
          border-left: 4px solid #27ae60;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          border-radius: 0 4px 4px 0;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 15px;
        }

        .gallery-image {
          width: 100%;
          height: 150px;
          object-fit: cover;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
        }

        .gallery-image:hover {
          transform: scale(1.05);
        }

        @media (max-width: 768px) {
          .club-main-info {
            flex-direction: column;
          }
          
          .club-image img {
            width: 100%;
            height: 200px;
          }
          
          .club-details-grid {
            grid-template-columns: 1fr;
          }
          
          .detail-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 5px;
          }
        }
      </style>
    </div>
  `
}

function ClubEdit() {
    const { id } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    // Redux 상태
    const { detail, update } = useSelector((state) => state.board)
    const loginData = useSelector((state) => state.login)

    // 권한 체크 상태
    const [hasPermission, setHasPermission] = useState(false)
    const [permissionChecked, setPermissionChecked] = useState(false)

    // 폼 상태
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        longDescription: "",
        imageUrl: "",
        galleryImages: [],
        members: 0,
        leader: "",
        meetingTime: "",
        meetingPlace: "",
        activities: [""],
    })

    const [errors, setErrors] = useState({})
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)

    // 카테고리 옵션
    // const categories = ["찬양", "봉사", "교육", "친교"]

    // 관리자 권한 체크 함수
    const checkAdminPermission = useCallback(() => {
        const { isLogin, isAuth, role, userRole, authority, token } = loginData || {}

        const authenticated = isLogin || isAuth || !!token
        if (!authenticated) {
            console.warn("❌ 로그인 상태 아님")
            return false
        }

        const userRoleValue = (role || userRole || authority || "").toUpperCase()
        console.log("🔑 사용자 역할:", userRoleValue)

        const adminRoles = ["ROLE_WEB_ADMIN", "ROLE_ADMIN", "WEB_ADMIN", "ADMIN"]
        const hasPermission = adminRoles.includes(userRoleValue)

        console.log("✅ 관리자 권한 여부:", hasPermission)
        return hasPermission
    }, [loginData])


    // 컴포넌트 마운트 시 권한 체크
    useEffect(() => {
        // 로그인 상태가 명확하지 않으면 아무것도 하지 않음
        if (!loginData || Object.keys(loginData).length === 0) return

        const permission = checkAdminPermission()

        setHasPermission(permission)
        setPermissionChecked(true)

        if (!permission) {
            alert("동아리 수정 권한이 없습니다.")
            navigate("/clubs", { replace: true })
            return
        }
    }, [checkAdminPermission, navigate, loginData]) // loginData 의존성 추가

    // 기존 데이터 로드
    useEffect(() => {
        if (id && hasPermission) {
            dispatch({
                type: actionTypes.BOARD_DETAIL_FETCH,
                payload: { id },
            })
        }

        return () => {
            dispatch({
                type: actionTypes.BOARD_DETAIL_RESET,
            })
        }
    }, [dispatch, id, hasPermission])

    // 데이터 로딩 완료 시 폼에 설정
    useEffect(() => {
        if (!detail.loading && detail.data && hasPermission) {
            try {
                // HTML에서 데이터 추출
                const parsedData = parseClubHTML(detail.data.contents)

                if (parsedData) {
                    setFormData({
                        name: parsedData.name || detail.data.title || "",
                        description: parsedData.description || "",
                        longDescription: parsedData.longDescription || "",
                        imageUrl: parsedData.imageUrl || "",
                        galleryImages: parsedData.galleryImages || [],
                        members: parsedData.members || 0,
                        leader: parsedData.leader || "",
                        meetingTime: parsedData.meetingTime || "",
                        meetingPlace: parsedData.meetingPlace || "",
                        activities: parsedData.activities.length > 0 ? parsedData.activities : [""],
                    })
                } else {
                    // 파싱 실패 시 기본값 설정
                    setFormData({
                        name: detail.data.title || "",
                        description: "",
                        longDescription: "",
                        imageUrl: "",
                        galleryImages: [],
                        members: 0,
                        leader: "",
                        meetingTime: "",
                        meetingPlace: "",
                        activities: [""],
                    })
                }

                setIsLoading(false)
            } catch (error) {
                console.error("데이터 파싱 오류:", error)
                setIsLoading(false)
            }
        } else if (!detail.loading && detail.error) {
            setIsLoading(false)
        }
    }, [detail.loading, detail.data, detail.error, hasPermission])

    // 수정 완료 후 처리
    useEffect(() => {
        if (update && update.success) {
            alert("동아리 정보가 수정되었습니다.")
            navigate(`/clubs/${id}`)
        }
    }, [update, navigate, id])

    const handleInputChange = useCallback(
        (field, value) => {
            setFormData((prev) => ({
                ...prev,
                [field]: value,
            }))
            // 에러 제거
            if (errors[field]) {
                setErrors((prev) => ({
                    ...prev,
                    [field]: "",
                }))
            }
        },
        [errors],
    )

    const handleArrayChange = useCallback((field, index, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field].map((item, i) => (i === index ? value : item)),
        }))
    }, [])

    const handleArrayAdd = useCallback((field, defaultValue = "") => {
        setFormData((prev) => ({
            ...prev,
            [field]: [...prev[field], defaultValue],
        }))
    }, [])

    const handleArrayRemove = useCallback((field, index) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index),
        }))
    }, [])

    const handleImageUpload = useCallback((e, isGallery = false) => {
        const file = e.target.files[0]
        if (file) {
            // 파일 크기 검증 (5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert("파일 크기는 5MB 이하여야 합니다.")
                return
            }

            // 파일 타입 검증
            if (!file.type.startsWith("image/")) {
                alert("이미지 파일만 업로드 가능합니다.")
                return
            }

            const reader = new FileReader()
            reader.onload = (event) => {
                const imageUrl = event.target.result
                if (isGallery) {
                    setFormData((prev) => ({
                        ...prev,
                        galleryImages: [...prev.galleryImages, imageUrl],
                    }))
                } else {
                    setFormData((prev) => ({
                        ...prev,
                        imageUrl: imageUrl,
                    }))
                }
            }
            reader.readAsDataURL(file)
        }
    }, [])

    const handleGalleryImageRemove = useCallback((index) => {
        setFormData((prev) => ({
            ...prev,
            galleryImages: prev.galleryImages.filter((_, i) => i !== index),
        }))
    }, [])

    const validateForm = () => {
        const newErrors = {}

        if (!formData.name.trim()) {
            newErrors.name = "동아리명을 입력해주세요."
        }

        if (!formData.description.trim()) {
            newErrors.description = "간단한 설명을 입력해주세요."
        }

        if (!formData.leader.trim()) {
            newErrors.leader = "리더명을 입력해주세요."
        }

        if (formData.members < 0) {
            newErrors.members = "회원 수는 0 이상이어야 합니다."
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        // 권한 재확인
        if (!checkAdminPermission()) {
            alert("동아리 수정 권한이 없습니다.")
            return
        }

        if (!validateForm()) {
            return
        }

        setIsSaving(true)

        try {
            // 동아리 데이터 정리
            const clubData = {
                name: formData.name,
                description: formData.description,
                longDescription: formData.longDescription,
                imageUrl: formData.imageUrl,
                galleryImages: formData.galleryImages,
                members: formData.members,
                leader: formData.leader,
                meetingTime: formData.meetingTime,
                meetingPlace: formData.meetingPlace,
                activities: formData.activities.filter((activity) => activity.trim()),
            }

            // HTML 템플릿 생성
            const htmlContent = generateClubHTML(clubData)

            const requestData = {
                boardId: Number.parseInt(id), // 수정할 게시물의 ID
                title: formData.name,
                contents: htmlContent,
                updateId: loginData.id || 1, // 수정하는 사용자 ID
            }


            dispatch({
                type: actionTypes.BOARD_UPDATE,
                payload: requestData,
            })
        } catch (error) {
            console.error("동아리 수정 오류:", error)
            alert("동아리 수정 중 오류가 발생했습니다.")
            setIsSaving(false)
        }
    }

    const handleBackClick = () => {
        navigate(`/clubs/${id}`)
    }

    // 권한 체크가 완료되지 않았으면 로딩 표시
    if (!permissionChecked) {
        return (
            <div className="club-edit-container">
                <div className="permission-check">
                    <h2>권한 확인 중...</h2>
                </div>
            </div>
        )
    }

    // 권한이 없으면 접근 차단
    if (!hasPermission) {
        return (
            <div className="club-edit-container">
                <div className="permission-denied">
                    <h2>접근 권한이 없습니다</h2>
                    <p>동아리 수정 권한이 필요합니다.</p>
                    <button onClick={handleBackClick} className="back-button">
                        돌아가기
                    </button>
                </div>
            </div>
        )
    }

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="loading-container">
                    <FaSpinner className="loading-spinner" />
                    <p>동아리 정보를 불러오는 중...</p>
                </div>
            )
        }

        if (detail.error) {
            return <EmptyState message="오류가 발생했습니다" description={detail.error} />
        }

        return (
            <div className="club-edit-content">
                <form onSubmit={handleSubmit} className="club-edit-form">
                    {/* 기본 정보 섹션 */}
                    <div className="form-section">
                        <h2>기본 정보</h2>
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="name">
                                    동아리명 <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange("name", e.target.value)}
                                    placeholder="동아리명을 입력하세요"
                                    className={errors.name ? "error" : ""}
                                />
                                {errors.name && <span className="error-message">{errors.name}</span>}
                            </div>

                            <div className="form-group full-width">
                                <label htmlFor="description">
                                    간단한 설명 <span className="required">*</span>
                                </label>
                                <textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => handleInputChange("description", e.target.value)}
                                    placeholder="동아리에 대한 간단한 설명을 입력하세요"
                                    rows="3"
                                    className={errors.description ? "error" : ""}
                                />
                                {errors.description && <span className="error-message">{errors.description}</span>}
                            </div>

                            <div className="form-group full-width">
                                <label htmlFor="longDescription">상세 설명</label>
                                <textarea
                                    id="longDescription"
                                    value={formData.longDescription}
                                    onChange={(e) => handleInputChange("longDescription", e.target.value)}
                                    placeholder="동아리에 대한 상세한 설명을 입력하세요"
                                    rows="5"
                                />
                            </div>
                        </div>
                    </div>

                    {/* 이미지 섹션 */}
                    <div className="form-section">
                        <h2>이미지</h2>
                        <div className="image-upload-section">
                            <div className="main-image-upload">
                                <label>대표 이미지</label>
                                <div className="image-upload-area">
                                    {formData.imageUrl ? (
                                        <div className="image-preview">
                                            <img src={formData.imageUrl || "/placeholder.svg"} alt="대표 이미지" />
                                            <button type="button" className="image-remove" onClick={() => handleInputChange("imageUrl", "")}>
                                                <FaTrash />
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="image-upload-label">
                                            <FaImage />
                                            <span>대표 이미지 업로드</span>
                                            <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, false)} />
                                        </label>
                                    )}
                                </div>
                            </div>

                            <div className="gallery-upload">
                                <label>갤러리 이미지</label>
                                <div className="gallery-grid">
                                    {formData.galleryImages.map((image, index) => (
                                        <div key={index} className="gallery-item">
                                            <img src={image || "/placeholder.svg"} alt={`갤러리 ${index + 1}`} />
                                            <button type="button" className="image-remove" onClick={() => handleGalleryImageRemove(index)}>
                                                <FaTrash />
                                            </button>
                                        </div>
                                    ))}
                                    <label className="gallery-upload-label">
                                        <FaPlus />
                                        <span>이미지 추가</span>
                                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, true)} />
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 상세 정보 섹션 */}
                    <div className="form-section">
                        <h2>상세 정보</h2>
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="members">
                                    <FaUsers /> 회원 수
                                </label>
                                <input
                                    type="number"
                                    id="members"
                                    value={formData.members}
                                    onChange={(e) => handleInputChange("members", Number.parseInt(e.target.value) || 0)}
                                    min="0"
                                    className={errors.members ? "error" : ""}
                                />
                                {errors.members && <span className="error-message">{errors.members}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="leader">
                                    리더명 <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="leader"
                                    value={formData.leader}
                                    onChange={(e) => handleInputChange("leader", e.target.value)}
                                    placeholder="리더명을 입력하세요"
                                    className={errors.leader ? "error" : ""}
                                />
                                {errors.leader && <span className="error-message">{errors.leader}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="meetingTime">
                                    <FaCalendarAlt /> 모임 시간
                                </label>
                                <input
                                    type="text"
                                    id="meetingTime"
                                    value={formData.meetingTime}
                                    onChange={(e) => handleInputChange("meetingTime", e.target.value)}
                                    placeholder="매주 토요일 오후 3시"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="meetingPlace">
                                    <FaMapMarkerAlt /> 모임 장소
                                </label>
                                <input
                                    type="text"
                                    id="meetingPlace"
                                    value={formData.meetingPlace}
                                    onChange={(e) => handleInputChange("meetingPlace", e.target.value)}
                                    placeholder="교회 3층 찬양실"
                                />
                            </div>
                        </div>
                    </div>

                    {/* 활동 내용 섹션 */}
                    <div className="form-section">
                        <h2>활동 내용</h2>
                        <div className="dynamic-list">
                            {formData.activities.map((activity, index) => (
                                <div key={index} className="dynamic-item">
                                    <input
                                        type="text"
                                        value={activity}
                                        onChange={(e) => handleArrayChange("activities", index, e.target.value)}
                                        placeholder="활동 내용을 입력하세요"
                                    />
                                    {formData.activities.length > 1 && (
                                        <button type="button" onClick={() => handleArrayRemove("activities", index)} className="remove-btn">
                                            <FaTrash />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button type="button" onClick={() => handleArrayAdd("activities")} className="add-btn">
                                <FaPlus /> 활동 추가
                            </button>
                        </div>
                    </div>

                    {/* 제출 버튼 */}
                    <div className="form-actions">
                        <button type="button" onClick={handleBackClick} className="cancel-btn">
                            취소
                        </button>
                        <button type="submit" disabled={isSaving || update?.loading} className="submit-btn">
                            <FaSave />
                            {isSaving || update?.loading ? "수정 중..." : "수정 완료"}
                        </button>
                    </div>

                    {update?.error && <div className="error-message">{update.error}</div>}
                </form>
            </div>
        )
    }

    return (
        <>
            <PC>
                <section className="pf-page">
                    <PageHeader title="동아리 수정" />
                    <div className="club-edit-container">
                        <div className="back-button-container">
                            <button className="back-button" onClick={handleBackClick}>
                                <FaArrowLeft /> 뒤로가기
                            </button>
                        </div>
                        {renderContent()}
                    </div>
                </section>
            </PC>

            <Notebook>
                <section className="pf-page">
                    <PageHeader title="동아리 수정" />
                    <div className="club-edit-container">
                        <div className="back-button-container">
                            <button className="back-button" onClick={handleBackClick}>
                                <FaArrowLeft /> 뒤로가기
                            </button>
                        </div>
                        {renderContent()}
                    </div>
                </section>
            </Notebook>

            <Tablet>
                <section className="pf-page">
                    <PageHeader title="동아리 수정" />
                    <div className="club-edit-container">
                        <div className="back-button-container">
                            <button className="back-button" onClick={handleBackClick}>
                                <FaArrowLeft /> 뒤로가기
                            </button>
                        </div>
                        {renderContent()}
                    </div>
                </section>
            </Tablet>

            <Mobile>
                <section className="mf-page">
                    <PageHeader title="동아리 수정" />
                    <div className="club-edit-container">
                        <div className="back-button-container">
                            <button className="back-button" onClick={handleBackClick}>
                                <FaArrowLeft /> 뒤로
                            </button>
                        </div>
                        {renderContent()}
                    </div>
                </section>
            </Mobile>

            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .club-edit-container {
                    padding: 20px;
                    max-width: 1280px;
                    width: 100%;
                    margin: 0 auto;
                }

                .back-button-container {
                    margin-bottom: 20px;
                }

                .back-button {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: none;
                    border: none;
                    color: #555;
                    font-size: 16px;
                    cursor: pointer;
                    padding: 8px 16px;
                    border-radius: 4px;
                    transition: all 0.2s;
                }

                .back-button:hover {
                    background-color: #f0f0f0;
                    color: #333;
                }

                .club-edit-content {
                    background-color: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                    padding: 30px;
                }

                .club-edit-form {
                    display: flex;
                    flex-direction: column;
                    gap: 30px;
                }

                .form-section {
                    border: 1px solid #e9ecef;
                    border-radius: 8px;
                    padding: 25px;
                    background: #f8f9fa;
                }

                .form-section h2 {
                    margin: 0 0 20px 0;
                    color: #2c3e50;
                    font-size: 1.4em;
                    font-weight: 600;
                    border-bottom: 2px solid #3498db;
                    padding-bottom: 8px;
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 20px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .form-group.full-width {
                    grid-column: 1 / -1;
                }

                .form-group label {
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .required {
                    color: #e74c3c;
                }

                .form-group input,
                .form-group select,
                .form-group textarea {
                    padding: 12px 16px;
                    border: 2px solid #e1e5e9;
                    border-radius: 8px;
                    font-size: 16px;
                    transition: border-color 0.2s;
                    background: white;
                }

                .form-group input:focus,
                .form-group select:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #3498db;
                    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
                }

                .form-group input.error,
                .form-group textarea.error {
                    border-color: #e74c3c;
                }

                .error-message {
                    color: #e74c3c;
                    font-size: 14px;
                    font-weight: 500;
                }

                /* 이미지 업로드 스타일 */
                .image-upload-section {
                    display: flex;
                    flex-direction: column;
                    gap: 25px;
                }

                .main-image-upload label {
                    font-weight: 600;
                    margin-bottom: 10px;
                    display: block;
                }

                .image-upload-area {
                    border: 2px dashed #bdc3c7;
                    border-radius: 8px;
                    padding: 20px;
                    text-align: center;
                    background: white;
                }

                .image-preview {
                    position: relative;
                    display: inline-block;
                }

                .image-preview img {
                    width: 200px;
                    height: 150px;
                    object-fit: cover;
                    border-radius: 8px;
                }

                .image-remove {
                    position: absolute;
                    top: -8px;
                    right: -8px;
                    background: #e74c3c;
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 24px;
                    height: 24px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .image-upload-label {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                    cursor: pointer;
                    color: #7f8c8d;
                    transition: color 0.2s;
                }

                .image-upload-label:hover {
                    color: #3498db;
                }

                .image-upload-label input {
                    display: none;
                }

                .gallery-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
                    gap: 15px;
                }

                .gallery-item {
                    position: relative;
                }

                .gallery-item img {
                    width: 100%;
                    height: 120px;
                    object-fit: cover;
                    border-radius: 8px;
                }

                .gallery-upload-label {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 120px;
                    border: 2px dashed #bdc3c7;
                    border-radius: 8px;
                    cursor: pointer;
                    color: #7f8c8d;
                    transition: all 0.2s;
                    background: white;
                }

                .gallery-upload-label:hover {
                    border-color: #3498db;
                    color: #3498db;
                }

                .gallery-upload-label input {
                    display: none;
                }

                /* 동적 리스트 스타일 */
                .dynamic-list {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }

                .dynamic-item {
                    display: flex;
                    gap: 10px;
                    align-items: center;
                }

                .dynamic-item input {
                    flex: 1;
                    padding: 12px 16px;
                    border: 2px solid #e1e5e9;
                    border-radius: 8px;
                    font-size: 16px;
                    background: white;
                }

                .remove-btn {
                    background: #e74c3c;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    padding: 12px;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .remove-btn:hover {
                    background: #c0392b;
                }

                .add-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #27ae60;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    padding: 12px 20px;
                    cursor: pointer;
                    font-weight: 600;
                    transition: background 0.2s;
                    align-self: flex-start;
                }

                .add-btn:hover {
                    background: #229954;
                }

                /* 액션 버튼 스타일 */
                .form-actions {
                    display: flex;
                    gap: 15px;
                    justify-content: flex-end;
                    padding-top: 20px;
                    border-top: 2px solid #ecf0f1;
                }

                .cancel-btn {
                    padding: 12px 24px;
                    border: 2px solid #bdc3c7;
                    background: white;
                    color: #7f8c8d;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 16px;
                    font-weight: 600;
                    transition: all 0.2s;
                }

                .cancel-btn:hover:not(:disabled) {
                    border-color: #95a5a6;
                    color: #2c3e50;
                }

                .submit-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 24px;
                    background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 16px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 15px rgba(52, 152, 219, 0.4);
                }

                .submit-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, #2980b9 0%, #3498db 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(52, 152, 219, 0.6);
                }

                .submit-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                    transform: none;
                }

                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 30px;
                    gap: 20px;
                }

                .loading-spinner {
                    font-size: 40px;
                    color: #3498db;
                    animation: spin 1s linear infinite;
                }

                .permission-check,
                .permission-denied {
                    text-align: center;
                    padding: 60px 30px;
                }

                @media (max-width: 768px) {
                    .club-edit-container {
                        padding: 15px;
                    }

                    .club-edit-content {
                        padding: 20px;
                    }

                    .form-grid {
                        grid-template-columns: 1fr;
                    }

                    .form-actions {
                        flex-direction: column;
                    }

                    .cancel-btn,
                    .submit-btn {
                        width: 100%;
                        justify-content: center;
                    }

                    .image-upload-section {
                        gap: 20px;
                    }

                    .gallery-grid {
                        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
                    }
                }
            `}</style>
        </>
    )
}

export default ClubEdit
